// BusinessLocationAutocomplete.js
import { useEffect, useRef, useState } from 'react';

// Add styles to ensure autocomplete shows above other elements
const autocompleteStyle = {
  '.pac-container': {
    zIndex: 9999,
    position: 'fixed'
  }
};

// Global promise to track Google Maps API loading
let googleMapsPromise = null;

// Function to load Google Maps API
const loadGoogleMapsAPI = () => {
  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    // Check if API is already loaded
    if (window.google && window.google.maps) {
      resolve(window.google.maps);
      return;
    }

    // Create script only if it doesn't exist
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google.maps));
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAAHKz5tes9kExmji4EOCRtq5IqSQmKR9w&libraries=places`;
    script.async = true;
    script.defer = true;

    script.addEventListener('load', () => {
      if (window.google && window.google.maps) {
        resolve(window.google.maps);
      } else {
        reject(new Error('Google Maps API loaded but google.maps not available'));
      }
    });

    script.addEventListener('error', () => {
      googleMapsPromise = null; // Reset promise on error
      reject(new Error('Failed to load Google Maps API'));
    });

    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

function BusinessLocationAutocomplete({ formData, setFormData, errors }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Add style injection
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .pac-container {
        z-index: 9999 !important;
        position: fixed;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Load Google Maps API
  useEffect(() => {
    let isMounted = true;

    const initializeGoogleMaps = async () => {
      try {
        await loadGoogleMapsAPI();
        if (!isMounted) return;
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading Google Maps API:', error);
        if (!isMounted) return;
        
        setLoadError('Failed to load location service');
        setIsLoading(false);
      }
    };

    initializeGoogleMaps();

    return () => {
      isMounted = false;
      if (autocompleteRef.current) {
        // Clean up event listeners if needed
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Initialize Autocomplete
  useEffect(() => {
    if (!isLoading && !loadError && window.google?.maps?.places && inputRef.current) {
      try {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ["(cities)"],
        });

        const placeChangedListener = autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          let country = '';
          let state = '';
          let city = '';
          let kecamatan = '';
          let postalCode = '';

          if (place.address_components) {
            for (const component of place.address_components) {
              const type = component.types[0];
              switch (type) {
                case 'country':
                  country = component.long_name;
                  break;
                case 'administrative_area_level_1':
                  state = component.long_name;
                  break;
                case 'administrative_area_level_2':
                  city = component.long_name;
                  break;
                case 'administrative_area_level_3':
                  kecamatan = component.long_name;
                  break;
                case 'locality':
                  city = component.long_name;
                  break;
                case 'postal_code':
                  postalCode = component.long_name;
                  break;
                default:
                  break;
              }
            }
          }

          setFormData(prev => ({
            ...prev,
            businessLocation: place.formatted_address || '',
            country,
            state,
            city,
            postalCode,
          }));
        });

        return () => {
          if (placeChangedListener) {
            window.google?.maps?.event?.removeListener(placeChangedListener);
          }
        };
      } catch (error) {
        console.error('Error initializing Google Maps Autocomplete:', error);
        setLoadError('Error initializing location autocomplete');
      }
    }
  }, [isLoading, loadError, setFormData]);

  return (
    <div className="mb-3">
      <label className="form-label">
        Business Location <span className="text-danger">*</span>
      </label>
      <input
        ref={inputRef}
        type="text"
        className={`form-control ${errors.businessLocation ? 'is-invalid' : ''}`}
        id="businessLocation"
        name="businessLocation"
        placeholder={isLoading ? 'Loading...' : 'Enter location'}
        value={formData.businessLocation || ''}
        onChange={e => setFormData(prev => ({ ...prev, businessLocation: e.target.value }))}
        disabled={isLoading}
      />
      {errors.businessLocation && (
        <div className="invalid-feedback">{errors.businessLocation}</div>
      )}
      {loadError && (
        <div className="invalid-feedback d-block">{loadError}</div>
      )}
      <div className="form-hint">
        {isLoading ? 'Loading location service...' : 'Start typing to get location suggestions and auto-fill address fields.'}
      </div>
    </div>
  );
}

export default BusinessLocationAutocomplete;

